import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoaderCircle } from "lucide-react";
import api from "../api/client";
import { useLangPrefix } from "../hooks/useLangPrefix";
import { formatApiError } from "../lib/apiErrorMessage";
import { useAuthStore } from "../store/authStore";
import LanguageSwitcher from "../components/layout/LanguageSwitcher";
import * as Sentry from "@sentry/react";

const COOLDOWN_SEC = 60;

function emptyDigits() {
  return ["", "", "", "", "", ""];
}

export default function VerifyOtpPage() {
  const { t } = useTranslation();
  const { prefix } = useLangPrefix();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applyAuthResponse = useAuthStore((s) => s.applyAuthResponse);
  const email = location.state?.email;
  const password = location.state?.password;
  const name = location.state?.name;
  const rawCallback =
    (typeof location.state?.callbackUrl === "string" && location.state.callbackUrl.trim()) ||
    (searchParams.get("callback_url")?.trim() ?? "");
  const safeCallbackUrl = useMemo(() => {
    if (!rawCallback) return "";
    if (rawCallback.startsWith("/") && !rawCallback.startsWith("//")) {
      return rawCallback;
    }
    return "";
  }, [rawCallback]);
  const nextPath = safeCallbackUrl || `${prefix}/dashboard`;
  const signupHref = safeCallbackUrl
    ? `${prefix}/signup?callback_url=${encodeURIComponent(safeCallbackUrl)}`
    : `${prefix}/signup`;
  const [digits, setDigits] = useState(emptyDigits);
  const [secondsLeft, setSecondsLeft] = useState(COOLDOWN_SEC);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const inputsRef = useRef([]);

  useEffect(() => {
    const id = setInterval(() => {
      setSecondsLeft((s) => (s <= 0 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const handleChange = (index, e) => {
    setError("");
    const v = e.target.value.replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = v;
      return next;
    });
    if (v && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index]) return;
      if (index > 0) {
        e.preventDefault();
        setDigits((prev) => {
          const next = [...prev];
          next[index - 1] = "";
          return next;
        });
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    setError("");
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    const arr = text.split("");
    const next = emptyDigits().map((_, i) => arr[i] || "");
    setDigits(next);
    const lastIdx = Math.min(arr.length - 1, 5);
    inputsRef.current[lastIdx]?.focus();
  };

  const handleResend = async () => {
    if (secondsLeft > 0 || resendLoading || !email) return;
    setResendLoading(true);
    setError("");
    try {
      await api.post("/manager/auth/send-otp", { email });
      setSecondsLeft(COOLDOWN_SEC);
    } catch (err) {
      if (!err?.response || err.response.status >= 500) {
        Sentry.captureException(err, { tags: { action: "resend_otp" }, extra: { email } });
      }
      setError(formatApiError(err, t("common.requestFailed")));
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    const code = digits.join("");
    if (code.length !== 6) {
      setError(t("verifyOtp.errorInvalidLength"));
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/manager/auth/signup", {
        email,
        password,
        otp: code,
        name,
      });
      await applyAuthResponse(res.data);
      navigate(nextPath, { replace: true });
    } catch (err) {
      if (!err?.response || err.response.status >= 500) {
        Sentry.captureException(err, { tags: { action: "verify_otp" }, extra: { email } });
      }
      setError(formatApiError(err, t("common.requestFailed")));
    } finally {
      setLoading(false);
    }
  };

  if (!email || !password || !name) {
    return <Navigate to={signupHref} replace />;
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-zinc-100 p-4 text-zinc-900 scheme-light">
      <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
        <LanguageSwitcher />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-100/70 via-transparent to-cyan-100/50" />

      <div className="w-full max-w-sm relative">
        <div className="text-center mb-8">
          <div className="inline-flex items-center">
            <img
              src="/full_logo.png"
              alt="Growchat"
              className="w-40 h-auto rounded-md object-cover"
            />
          </div>
          <p className="text-sm text-gray-500">{t("login.tagline")}</p>
        </div>

        <div className="card p-6">
          <h2 className="mb-2 text-center text-lg font-semibold text-zinc-900">
            {t("verifyOtp.title")}
          </h2>
          <p className="mb-6 text-center text-sm text-gray-600">
            {t("verifyOtp.instructionPrefix")}{" "}
            <span className="break-all font-medium text-zinc-900">{email}</span>
          </p>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-5">
            <div className="flex gap-2 justify-center">
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputsRef.current[i] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleChange(i, e)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onPaste={handlePaste}
                  className="h-11 w-10 rounded-lg border border-zinc-300 bg-white text-center text-lg font-medium text-zinc-900 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 sm:w-11"
                />
              ))}
            </div>

            <p className="text-center text-xs text-gray-500">
              {t("verifyOtp.resendPrompt")}{" "}
              {secondsLeft > 0 ? (
                <span className="text-gray-600">
                  {t("verifyOtp.resendIn", { seconds: secondsLeft })}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
                >
                  {resendLoading
                    ? t("verifyOtp.resending")
                    : t("verifyOtp.resendButton")}
                </button>
              )}
            </p>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center py-2.5 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Spinner /> {t("verifyOtp.submitting")}
                </span>
              ) : (
                t("verifyOtp.submit")
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-500 mt-5">
            {t("verifyOtp.wrongEmail")}{" "}
            <Link
              to={signupHref}
              className="font-medium text-indigo-600 hover:text-indigo-700"
            >
              {t("verifyOtp.backToSignUp")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Spinner() {
  return <LoaderCircle className="h-4 w-4 animate-spin" />;
}
